// src/components/GalleryCarousel.jsx
import React from 'react';
import styled from 'styled-components';

const Wrap = styled.div`
  position: relative;
  border: 1px solid var(--bdr);
  border-radius: 16px;
  background: var(--card);
  overflow: hidden;
  box-shadow: var(--shadow);
  isolation: isolate;
`;

const Viewport = styled.div`
  width: 100%;
  overflow: hidden;
  touch-action: pan-y;
`;

const Track = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  will-change: transform;
  transition: transform .35s ease;
  transform: translateX(${p => `-${p.$index * 100}%`});
`;

const Slide = styled.div`
  position: relative;
  width: 100%;
  flex: 0 0 100%;
  aspect-ratio: ${p => p.$ratio || '16/9'};
  background: #111;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display:block;
    user-select: none;
    -webkit-user-drag: none;
    pointer-events: none;
    cursor: zoom-in;
  }
`;

const Arrow = styled.button`
  position: absolute;
  top: 50%;
  width:42px; height:42px;
  border-radius: 999px;
  border: 1px solid var(--bdr);
  background: rgba(0,0,0,.35);
  color: #fff;
  box-shadow: var(--shadow);
  display:grid; place-items:center;
  backdrop-filter: blur(6px);
  opacity: .95;
  cursor: pointer;
  z-index: 5;
  line-height: 1;
  font-size: 20px;
  transform: translateY(-50%) translateZ(0);
  backface-visibility: hidden;
  will-change: transform;
  outline: none;
  &:hover,
  &:active,
  &:focus,
  &:focus-visible {
    transform: translateY(-50%) translateZ(0);
  }
  transition: background .2s ease; /* без изменения тени/границы, чтобы не было подрагивания */
  &:hover { background: rgba(0,0,0,.5); }
  &:hover { box-shadow: var(--shadow) !important; }
  &:focus-visible { outline: 2px solid rgba(255,255,255,.8); outline-offset: 2px; }
  &:disabled { opacity: .5; cursor: not-allowed; }
  ${p => p.$left ? 'left: 10px;' : 'right: 10px;'}
`;

const Dots = styled.div`
  position: absolute; left: 0; right: 0; bottom: 10px;
  display: flex; gap: 6px; justify-content: center;
  z-index: 4;
`;

const Dot = styled.button`
  width: 8px; height: 8px; border-radius: 999px;
  border: 1px solid rgba(255,255,255,.6);
  background: ${p => p['data-active'] ? 'rgba(255,255,255,.95)' : 'rgba(0,0,0,.15)'};
  box-shadow: var(--shadow);
  cursor: pointer;
  outline: none;
  transition: background .15s ease;
  transform: none !important;
  &:hover{
    background: ${p => p['data-active'] ? 'rgba(255,255,255,.95)' : 'rgba(255,255,255,.25)'};
    transform: none !important;
    box-shadow: var(--shadow) !important;
  }
  &:focus-visible { outline: 2px solid rgba(255,255,255,.8); outline-offset: 2px; }
`;

const Placeholder = styled.div`
  aspect-ratio: ${p => p.$ratio || '16/9'};
  display:grid; place-items:center;
  color: var(--muted);
  background: #000;
`;

/* === ЛАЙТБОКС === */
const Lightbox = styled.div`
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,.92);
  display: grid; place-items: center;
`;
const LBInner = styled.div`
  width: min(1200px, 96vw);
  height: min(92vh, 900px);
  position: relative;
`;
const LBImg = styled.img`
  width: 100%; height: 100%; object-fit: contain; display: block;
`;
const LBClose = styled.button`
  position:absolute; top:12px; right:12px;
  width:42px; height:42px; border-radius:999px; border:1px solid rgba(255,255,255,.25);
  background: rgba(0,0,0,.45); color:#fff; font-size:20px; cursor:pointer; box-shadow: var(--shadow);
`;
const LBArrow = styled.button`
  position:absolute; top:50%;
  width:48px; height:48px; border-radius:999px; border:1px solid rgba(255,255,255,.25);
  background: rgba(0,0,0,.45); color:#fff; font-size:24px; cursor:pointer; box-shadow: var(--shadow);
  transform: translateY(-50%);
  ${p => p.$left ? 'left:12px' : 'right:12px'};
`;

export default function GalleryCarousel({
  images = [],
  ratio = '16/9',
  startIndex = 0,
  onIndexChange,
  altPrefix = 'Photo'
}) {
  const list = Array.isArray(images) ? images.filter(Boolean) : [];
  const [index, setIndex] = React.useState(
    Math.min(Math.max(0, startIndex|0), Math.max(0, list.length - 1))
  );
  const [lbOpen, setLbOpen] = React.useState(false);
  const [lbIndex, setLbIndex] = React.useState(0);

  const trackRef = React.useRef(null);
  const wrapRef  = React.useRef(null);

  // свайп только для тач-устройств
  const drag = React.useRef({ active:false, startX:0, dx:0, width:0 });

  const goto = React.useCallback((i) => {
    const next = Math.min(Math.max(i, 0), Math.max(0, list.length - 1));
    setIndex(next);
    onIndexChange?.(next);
  }, [list.length, onIndexChange]);

  const prev = React.useCallback(() => goto(index - 1), [goto, index]);
  const next = React.useCallback(() => goto(index + 1), [goto, index]);

  const onPointerDown = (e) => {
    if (e.pointerType !== 'touch') return;
    if (!wrapRef.current) return;
    wrapRef.current.setPointerCapture?.(e.pointerId);
    drag.current.active = true;
    drag.current.startX = e.clientX;
    drag.current.dx = 0;
    drag.current.width = wrapRef.current.clientWidth || 1;
  };

  const onPointerMove = (e) => {
    if (e.pointerType !== 'touch') return;
    if (!drag.current.active || !trackRef.current) return;
    drag.current.dx = e.clientX - drag.current.startX;
    trackRef.current.style.transition = 'none';
    const base = -index * drag.current.width;
    trackRef.current.style.transform = `translateX(${base + drag.current.dx}px)`;
  };

  const endDrag = () => {
    if (!drag.current.active || !trackRef.current) return;
    const { dx, width } = drag.current;
    drag.current.active = false;
    trackRef.current.style.transition = '';
    const threshold = Math.max(40, width * 0.12);
    if (dx > threshold)      prev();
    else if (dx < -threshold) next();
    else trackRef.current.style.transform = '';
  };

  const onPointerUp = (e) => { if (e.pointerType === 'touch') endDrag(); };
  const onPointerCancel = (e) => { if (e.pointerType === 'touch') endDrag(); };

  // клавиатурная навигация
  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); prev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [prev, next]);

  // Блокируем скролл body при открытом лайтбоксе
  React.useEffect(() => {
    if (!lbOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [lbOpen]);

  // ESC/стрелки в лайтбоксе — ВАЖНО: хук должен быть выше любого раннего return
  React.useEffect(() => {
    if (!lbOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLbOpen(false);
      if (e.key === 'ArrowLeft') setLbIndex(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setLbIndex(i => Math.min((list.length || 1) - 1, i + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lbOpen, list.length]);

  // РАННИЙ РЕНДЕР ПЛЕЙСХОЛДЕРА — после всех хуков
  if (!list.length) {
    return (
      <Wrap>
        <Placeholder $ratio={ratio}>
          Нет изображений
        </Placeholder>
      </Wrap>
    );
  }

  const canPrev = index > 0;
  const canNext = index < list.length - 1;

  const openLightbox = (i) => { setLbIndex(i); setLbOpen(true); };
  const closeLightbox = () => setLbOpen(false);
  const lbPrev = () => setLbIndex(i => Math.max(0, i - 1));
  const lbNext = () => setLbIndex(i => Math.min(list.length - 1, i + 1));

  return (
    <Wrap ref={wrapRef} tabIndex={0} aria-roledescription="carousel" aria-label="gallery">
      <Viewport
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        <Track ref={trackRef} $index={index}>
          {list.map((src, i) => (
            <Slide key={`${src}_${i}`} $ratio={ratio} onClick={() => openLightbox(i)}>
              <img
                src={src}
                alt={`${altPrefix} ${i + 1}`}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
              />
            </Slide>
          ))}
        </Track>
      </Viewport>

      {list.length > 1 && (
        <>
          <Arrow
            $left
            onClick={prev}
            aria-label="Предыдущий"
            disabled={!canPrev}
            title="Предыдущий"
          >
            ‹
          </Arrow>
          <Arrow
            onClick={next}
            aria-label="Следующий"
            disabled={!canNext}
            title="Следующий"
          >
            ›
          </Arrow>

          <Dots>
            {list.map((_, i) => (
              <Dot
                key={i}
                data-active={i === index}
                aria-label={`Перейти к слайду ${i+1}`}
                onClick={() => setIndex(i)}
                title={`#${i+1}`}
              />
            ))}
          </Dots>
        </>
      )}

      {lbOpen && (
        <Lightbox onClick={closeLightbox}>
          <LBInner onClick={(e)=>e.stopPropagation()}>
            <LBClose onClick={closeLightbox} aria-label="Закрыть">✕</LBClose>
            {lbIndex > 0 && <LBArrow $left onClick={lbPrev} aria-label="Назад">‹</LBArrow>}
            {lbIndex < list.length - 1 && <LBArrow onClick={lbNext} aria-label="Вперёд">›</LBArrow>}
            <LBImg src={list[lbIndex]} alt={`image ${lbIndex+1}`} />
          </LBInner>
        </Lightbox>
      )}
    </Wrap>
  );
}
